import { Injectable } from '@angular/core';
import { ApiService, AppSettingsService, StatefulService } from '@woolworthsnz/styleguide';
import {
	OrderConfirmationResponse,
	OrderViewModel,
	ProductResponse,
	CategoryResponse,
	ProductTag,
} from '@woolworthsnz/trader-api';
import { getAnalyticsData } from '@woolworthsnz/analytics';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

export interface OrderConfirmationState {
	order: OrderViewModel;
	products: ProductResponse[];
	categorizedProducts: CategoryResponse[] | undefined;
	canFirePurchaseEvent: boolean;
	couponIDs: string;
}
const initialState: OrderConfirmationState | undefined = undefined;

@Injectable({ providedIn: 'root' })
export class OrderConfirmationService extends StatefulService<OrderConfirmationState | undefined> {
	constructor(
		private apiService: ApiService,
		private appSettingsService: AppSettingsService
	) {
		super(initialState);
	}

	public fetchData(orderId: number | undefined): Observable<OrderConfirmationState | undefined> {
		this.setState(undefined);
		return this.fetchOrderData(orderId).pipe(
			map((orderConfirmationResponse: OrderConfirmationResponse): OrderConfirmationState | undefined => {
				const { order, canFirePurchaseEvent, couponIDs } = orderConfirmationResponse;
				const categorizedProducts = orderConfirmationResponse.items;
				const flattenCategorizedProducts = categorizedProducts?.map((category) => category.products);
				const products = flattenCategorizedProducts?.flat() as ProductResponse[];
				this.setState({ order, products, categorizedProducts, canFirePurchaseEvent, couponIDs });
				return this.state;
			})
		);
	}

	public fetchOrderData(orderId?: number | undefined): Observable<OrderConfirmationResponse> {
		const endPoint = this.appSettingsService.getEndpoint('orderConfirmation');
		const orderPath = !!orderId ? `/${orderId}` : '';

		return this.apiService.get(endPoint + orderPath);
	}

	public getAnalyticsData(): object {
		const boostOfferProducts = (this.state?.products || []).filter(
			(product) => product.productTag?.tagType === ProductTag.TagTypeEnum.IsBoostOffer
		);
		return {
			order_id: this.state?.order?.id || '',
			order_shipping: this.state?.order?.totals?.deliveryFee || '',
			order_total: this.state?.order?.totals?.amountToPay || '',
			...getAnalyticsData(this.state?.products),
			number_of_boosts: boostOfferProducts.length,
			boosts_value: boostOfferProducts.reduce(
				(acc, product: ProductResponse) => acc + this.currencyToNumber(product.price?.total || '0'),
				0
			),
		};
	}

	private currencyToNumber(value: string): number {
		return Number(value.substring(1));
	}
}
